import type { PayloadAction } from '@reduxjs/toolkit'
import { combineEpics } from 'redux-observable'
import { from, of } from 'rxjs'
import { catchError, filter, mergeMap, switchMap } from 'rxjs/operators'
import { getComparativeTableV2 } from '@/api/kubik/tender/clients/axios/getComparativeTableV2'
import { getParticipantInfo } from '@/api/kubik/tender/clients/axios/getParticipantInfo'
import { getTenderDetailedInfo } from '@/api/kubik/tender/clients/axios/getTenderDetailedInfo'
import type { Epic } from '@/logic/interfaces'
import {
  setComparativeTable,
  setComparativeTableLoading,
  setError,
  setParticipantInfo,
  setParticipantInfoLoading,
  setTender,
  setTenderLoading,
} from './reducer'

export const fetchTenderEpic: Epic<number, typeof setTender> = (action$, _state$) =>
  action$.pipe(
    filter((action: PayloadAction<unknown>): action is PayloadAction<number> => action.type === 'tender/fetchTender'),
    switchMap(action =>
      of(setTenderLoading(true)).pipe(
        switchMap(() =>
          from(getTenderDetailedInfo({ tenderId: action.payload })).pipe(
            mergeMap(response => [setTenderLoading(false), setTender(response)]),
            catchError(error => from([setError(error.message), setTenderLoading(false)])),
          ),
        ),
      ),
    ),
  )

export const fetchComparativeTableEpic: Epic<number, typeof setComparativeTable> = (action$, _state$) =>
  action$.pipe(
    filter((action: PayloadAction<unknown>): action is PayloadAction<number> => action.type === 'tender/fetchComparativeTable'),
    switchMap(action =>
      of(setComparativeTableLoading(true)).pipe(
        switchMap(() =>
          from(getComparativeTableV2({ tenderId: action.payload })).pipe(
            mergeMap(response => [setComparativeTableLoading(false), setComparativeTable(response.data || null)]),
            catchError(error => from([setError(error.message), setComparativeTableLoading(false)])),
          ),
        ),
      ),
    ),
  )

export const fetchParticipantInfoEpic: Epic<number, typeof setParticipantInfo> = (action$, _state$) =>
  action$.pipe(
    filter((action: PayloadAction<unknown>): action is PayloadAction<number> => action.type === 'tender/fetchParticipantInfo'),
    switchMap(action =>
      of(setParticipantInfoLoading(true)).pipe(
        switchMap(() =>
          from(getParticipantInfo({ tenderId: action.payload })).pipe(
            mergeMap(response => [setParticipantInfoLoading(false), setParticipantInfo(response.data || null)]),
            catchError(error => from([setError(error.message), setParticipantInfoLoading(false)])),
          ),
        ),
      ),
    ),
  )

export default combineEpics(fetchTenderEpic, fetchComparativeTableEpic, fetchParticipantInfoEpic)
